/* Store locator — Google Maps + postal code distance search.
 *
 * Loaded as a deferred script. Google Maps JS bootstraps `hyspecsStoreLocatorInit`
 * via its `callback` parameter once the API is ready. The DOM is also ready by
 * then (defer guarantees that), so we initialise the map, render markers, and
 * wire up search + filters in one pass.
 */
(function () {
  'use strict';

  var state = {
    map: null,
    markers: [],
    geocoder: null,
    userMarker: null,
    stores: [],
    cards: [],
    matchIds: null,
    defaultCenter: { lat: 56.13, lng: -106.34 },
    defaultZoom: 4,
  };

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* Haversine distance in km between two lat/lng pairs. */
  function distanceKm(a, b) {
    var R = 6371;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var lat1 = toRad(a.lat);
    var lat2 = toRad(b.lat);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function loadDataset() {
    var node = document.getElementById('store-locator-data');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error('[store-locator] dataset parse failed', e);
      return null;
    }
  }

  /* Minimal light map style — neutral landscape, hidden POIs/transit, brass-tinted water. */
  var MAP_STYLE = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dde6ec' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7d8a92' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#fcfcfb' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#f4f4f3' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d5d5d3' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cfcfcf' }] },
  ];

  function brandedMarkerIcon(featured) {
    /* Custom SVG marker — brass fill on featured, dark on normal. */
    var fill = featured ? '#BF9B30' : '#141414';
    var stroke = '#FCFCFB';
    return {
      path: 'M12 2a8 8 0 0 0-8 8c0 6 8 12 8 12s8-6 8-12a8 8 0 0 0-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
      fillColor: fill,
      fillOpacity: 1,
      strokeColor: stroke,
      strokeWeight: 1.5,
      scale: 1.6,
      anchor: new google.maps.Point(12, 22),
      labelOrigin: new google.maps.Point(12, 9),
    };
  }

  function buildMap() {
    var mapEl = $('.js-store-locator-map');
    if (!mapEl) return null;

    var map = new google.maps.Map(mapEl, {
      center: state.defaultCenter,
      zoom: state.defaultZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      styles: MAP_STYLE,
      backgroundColor: '#fcfcfb',
    });

    state.infoWindow = new google.maps.InfoWindow({
      pixelOffset: new google.maps.Size(0, -4),
    });

    var bounds = new google.maps.LatLngBounds();
    state.markers = state.stores
      .filter(function (s) { return s.lat && s.lng; })
      .map(function (store) {
        var pos = { lat: Number(store.lat), lng: Number(store.lng) };
        var marker = new google.maps.Marker({
          position: pos,
          map: map,
          title: store.name,
          icon: brandedMarkerIcon(store.featured),
        });
        marker.addListener('click', function () {
          showInfoFor(store);
          highlightStore(store.id);
        });
        store._marker = marker;
        bounds.extend(pos);
        return marker;
      });

    return map;
  }

  function showInfoFor(store) {
    if (!state.infoWindow || !store._marker) return;
    var dirUrl = 'https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng;
    var html =
      '<div style="font-family:inherit;font-size:13px;line-height:1.4;max-width:220px;">' +
      '<strong style="color:#141414;font-size:14px;display:block;margin-bottom:4px;">' + escapeHtml(store.name) + '</strong>' +
      '<div style="color:#6B6B68;">' + escapeHtml(store.address) + '<br>' + escapeHtml(store.city) + ', ' + escapeHtml(store.province) + ' ' + escapeHtml(store.postal) + '</div>' +
      (store.phone ? '<div style="margin-top:4px;"><a href="tel:' + store.phone.replace(/[^0-9+]/g, '') + '" style="color:#BF9B30;">' + escapeHtml(store.phone) + '</a></div>' : '') +
      '<div style="margin-top:6px;"><a href="' + dirUrl + '" target="_blank" rel="noopener" style="color:#BF9B30;font-weight:600;">Get directions →</a></div>' +
      '</div>';
    state.infoWindow.setContent(html);
    state.infoWindow.open({ map: state.map, anchor: store._marker });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function highlightStore(id) {
    state.cards.forEach(function (card) {
      card.classList.toggle('is-highlighted', card.dataset.id === id);
    });
    var card = state.cards.find(function (c) { return c.dataset.id === id; });
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /* Visibility is driven solely by a store-name search: everything shows
     until one narrows the list. (The category chips were removed, so there is
     no second filter to reconcile against.) */
  function applyVisibility() {
    var ids = state.matchIds;
    state.cards.forEach(function (card) {
      card.hidden = !(!ids || ids[card.dataset.id] === true);
    });
    state.stores.forEach(function (store) {
      if (!store._marker) return;
      var visible = !ids || ids[store.id] === true;
      store._marker.setMap(visible ? state.map : null);
    });
  }

  function clearTextMatches() {
    if (!state.matchIds) return;
    state.matchIds = null;
    applyVisibility();
  }

  function setStatus(message, isError) {
    var el = $('.js-store-locator-status');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('is-error', !!isError);
  }

  function showReset(show) {
    var btn = $('.js-store-locator-reset');
    if (btn) btn.hidden = !show;
  }

  function reorderCards(rank) {
    var list = $('.js-store-locator-list');
    if (!list) return;
    var sorted = state.cards.slice().sort(function (a, b) {
      var ar = rank[a.dataset.id];
      var br = rank[b.dataset.id];
      if (ar == null && br == null) return 0;
      if (ar == null) return 1;
      if (br == null) return -1;
      return ar - br;
    });
    sorted.forEach(function (card) { list.appendChild(card); });
  }

  function clearDistances() {
    state.cards.forEach(function (card) {
      var el = card.querySelector('.js-store-card-distance');
      if (el) {
        el.hidden = true;
        el.textContent = '';
      }
    });
  }

  function setDistance(card, km) {
    var el = card.querySelector('.js-store-card-distance');
    if (!el) return;
    el.hidden = false;
    el.textContent = km < 1 ? Math.round(km * 1000) + ' m' : km.toFixed(1) + ' km';
  }

  /* Accent- and punctuation-insensitive key, so "Equipements", "Riviere-du-Loup"
     and "riviere du loup" all match the stored accented spellings. */
  function normalize(value) {
    return String(value == null ? '' : value)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function storeCoords(store) {
    var lat = Number(store.lat);
    var lng = Number(store.lng);
    return lat && lng ? { lat: lat, lng: lng } : null;
  }

  /* One search box, several kinds of input, resolved in this order:
       1. a postal code or ZIP -> geocode, then sort every store by distance
       2. a city or province we already stock -> that store becomes the origin,
          so the city comes back first and the rest sort around it
       3. a store name, or part of one -> show just those locations
       4. anything else -> geocoded, so the nearest store still wins
     Cities are checked BEFORE names on purpose: our store names carry their
     city ("Prime Fasteners — Calgary"), so a name-first order would answer
     "Calgary" with that one branch and hide every alternative nearby.
     Local matching runs before Google, so places and names we already stock
     keep working even when geocoding is unavailable. */
  function runSearch(query) {
    var q = String(query == null ? '' : query).trim();
    if (!q) {
      setStatus('Enter a postal code, city or store name to search.', true);
      return;
    }

    var compact = q.replace(/\s+/g, '').toUpperCase();
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) { geocodePostal(q, 'CA'); return; }
    if (/^\d{5}(-?\d{4})?$/.test(compact)) { geocodePostal(q, 'US'); return; }

    var nq = normalize(q);
    if (!nq) {
      setStatus('Enter a postal code, city or store name to search.', true);
      return;
    }

    // 2. A place we stock. Province is matched whole ("AB"), city as a
    //    substring so "sylvan" finds "Sylvan Lake".
    var placeMatch = findStore(function (store) {
      return normalize(store.city).indexOf(nq) !== -1 || normalize(store.province) === nq;
    });
    if (placeMatch) {
      clearTextMatches();
      sortByDistance(storeCoords(placeMatch));
      return;
    }

    // 3. A store name.
    var nameMatches = state.stores.filter(function (store) {
      return normalize(store.name).indexOf(nq) !== -1;
    });
    if (nameMatches.length) { showNameMatches(nameMatches, q); return; }

    // 4. A street we stock, then finally Google.
    var addressMatch = findStore(function (store) {
      return normalize(store.address).indexOf(nq) !== -1;
    });
    if (addressMatch) {
      clearTextMatches();
      sortByDistance(storeCoords(addressMatch));
      return;
    }

    geocodePlace(q);
  }

  /* First store satisfying `test` that also has usable coordinates. */
  function findStore(test) {
    for (var i = 0; i < state.stores.length; i++) {
      if (test(state.stores[i]) && storeCoords(state.stores[i])) return state.stores[i];
    }
    return null;
  }

  function geocoderReady() {
    if (state.geocoder) return true;
    setStatus('Search is unavailable right now — browse the retailers below.', true);
    return false;
  }

  function geocodePostal(query, country) {
    if (!geocoderReady()) return;
    setStatus('Searching…');
    state.geocoder.geocode(
      { address: query, componentRestrictions: { country: country, postalCode: query } },
      function (results, status) {
        if (status !== 'OK' || !results || results.length === 0) {
          setStatus('No location found for "' + query + '". Check the postal code or ZIP and try again.', true);
          return;
        }
        acceptOrigin(results[0]);
      }
    );
  }

  /* Free-text place lookup. Two Google quirks drive the shape of this:
     `componentRestrictions.country` must be a single country (pass an array
     and the callback never fires, so the status would hang on "Searching…"),
     and a CA-restricted lookup of a US city answers OK with a country-level
     hit for "Canada" rather than ZERO_RESULTS. So try each country in turn
     and ignore anything that resolves no finer than a country. */
  function geocodePlace(query) {
    if (!geocoderReady()) return;
    var countries = ['CA', 'US'];
    var i = 0;

    function attempt() {
      if (i >= countries.length) {
        setStatus('Nothing found for "' + query + '". Try a postal code, a city, or a store name.', true);
        return;
      }
      state.geocoder.geocode(
        { address: query, componentRestrictions: { country: countries[i++] } },
        function (results, status) {
          var hit = status === 'OK' && results && results[0];
          if (hit && results[0].types.indexOf('country') === -1) {
            acceptOrigin(results[0]);
            return;
          }
          attempt();
        }
      );
    }

    setStatus('Searching…');
    attempt();
  }

  function acceptOrigin(result) {
    var loc = result.geometry.location;
    clearTextMatches();
    sortByDistance({ lat: loc.lat(), lng: loc.lng() });
  }

  /* A name search narrows the list to the matching locations instead of
     sorting by distance — "Prime" should show all seven Prime branches. */
  function showNameMatches(matches, query) {
    var ids = {};
    matches.forEach(function (store) { ids[store.id] = true; });
    state.matchIds = ids;

    clearDistances();
    applyVisibility();
    showReset(true);

    if (state.map) {
      if (state.userMarker) {
        state.userMarker.setMap(null);
        state.userMarker = null;
      }
      var bounds = new google.maps.LatLngBounds();
      var located = 0;
      matches.forEach(function (store) {
        var c = storeCoords(store);
        if (c) { bounds.extend(c); located += 1; }
      });
      if (located) {
        state.map.fitBounds(bounds, 48);
        if (located === 1) state.map.setZoom(Math.min(state.map.getZoom(), 13));
      }
    }

    if (matches.length === 1) {
      var only = matches[0];
      var where = [only.city, only.province].filter(Boolean).join(', ');
      setStatus(where ? only.name + ' — ' + where : only.name);
    } else {
      setStatus(matches.length + ' retailers matching "' + query + '"');
    }
  }

  function sortByDistance(origin) {
    var distances = state.stores.map(function (store) {
      return {
        id: store.id,
        km: store.lat && store.lng
          ? distanceKm(origin, { lat: Number(store.lat), lng: Number(store.lng) })
          : Infinity,
      };
    });
    distances.sort(function (a, b) { return a.km - b.km; });

    var rank = {};
    distances.forEach(function (d, i) { rank[d.id] = i; });
    reorderCards(rank);

    state.cards.forEach(function (card) {
      var d = distances.find(function (x) { return x.id === card.dataset.id; });
      if (d && isFinite(d.km)) setDistance(card, d.km);
    });

    if (state.map) {
      var bounds = new google.maps.LatLngBounds();
      bounds.extend(origin);
      var nearest = distances.filter(function (d) { return isFinite(d.km); }).slice(0, 5);
      nearest.forEach(function (d) {
        var s = state.stores.find(function (x) { return x.id === d.id; });
        if (s) bounds.extend({ lat: Number(s.lat), lng: Number(s.lng) });
      });
      state.map.fitBounds(bounds, 48);

      if (state.userMarker) state.userMarker.setMap(null);
      state.userMarker = new google.maps.Marker({
        position: origin,
        map: state.map,
        title: 'Your location',
        zIndex: 999,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#141414',
          fillOpacity: 1,
          strokeColor: '#FCFCFB',
          strokeWeight: 3,
        },
      });
    }

    var nearestStore = state.stores.find(function (s) { return s.id === distances[0].id; });
    setStatus(
      nearestStore
        ? 'Nearest: ' + nearestStore.name + ' — ' + distances[0].km.toFixed(1) + ' km away'
        : 'Showing all retailers, sorted by distance.'
    );
    showReset(true);
  }

  function reset() {
    var input = $('#store-locator-postal');
    if (input) input.value = '';
    setStatus('');
    showReset(false);
    clearDistances();
    state.matchIds = null;
    applyVisibility();

    var list = $('.js-store-locator-list');
    if (list) state.cards.forEach(function (card) { list.appendChild(card); });

    if (state.userMarker) {
      state.userMarker.setMap(null);
      state.userMarker = null;
    }
    if (state.map) {
      state.map.setCenter(state.defaultCenter);
      state.map.setZoom(state.defaultZoom);
    }
  }

  function wireEvents() {
    var form = $('.js-store-locator-search');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = $('#store-locator-postal');
        runSearch(input ? input.value : '');
      });
    }

    var resetBtn = $('.js-store-locator-reset');
    if (resetBtn) resetBtn.addEventListener('click', reset);

    /* Card click → focus on map + open info window. Anchor/button clicks
       inside the card still work (we let them bubble out). */
    state.cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('a, button, summary')) return;
        var id = card.dataset.id;
        var store = state.stores.find(function (s) { return s.id === id; });
        if (!store || !state.map || !store._marker) return;
        state.map.panTo(store._marker.getPosition());
        if (state.map.getZoom() < 11) state.map.setZoom(11);
        showInfoFor(store);
        highlightStore(id);
      });
    });
  }

  /* Called by Google Maps JS via `?callback=` once the API is ready. */
  window.hyspecsStoreLocatorInit = function () {
    if (state.map) return; // already initialised
    var data = loadDataset();
    if (!data) return;
    state.stores = data.stores || [];
    state.defaultCenter = data.default_center || state.defaultCenter;
    state.defaultZoom = data.default_zoom || state.defaultZoom;
    state.cards = $all('.js-store-card');

    if (typeof google === 'undefined' || !google.maps) {
      console.warn('[store-locator] Google Maps not available');
      return;
    }
    state.geocoder = new google.maps.Geocoder();
    state.map = buildMap();
    wireEvents();
  };

  /* The Maps script is `async`, so it can finish BEFORE this deferred file
     runs. When it does, its `?callback=` fires against a callback that does
     not exist yet ("hyspecsStoreLocatorInit is not a function") and the map,
     the postal search and the name search are all dead for that page view.
     If Maps is already up by the time we get here, initialise ourselves
     instead of waiting for a callback that has already been and gone. */
  if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
    window.hyspecsStoreLocatorInit();
  }

  // Wire form events even without Google Maps so the UI degrades gracefully.
  document.addEventListener('DOMContentLoaded', function () {
    if (!state.map) {
      state.cards = $all('.js-store-card');
    }
  });
})();
